import { z } from 'zod'

/**
 * Cart and checkout-session contracts shared by the web bag drawer and the API
 * (R-BRAND-02, R-ARCH-06). The browser never sends a price: a line carries only
 * what the buyer chose (a birthstone bottle by month and line, or a special
 * edition by id, plus optional engraving and box signature) and the API prices
 * every line from the `@mist/content` constants through `priceCartLineCents`,
 * so web and API can never disagree on an amount.
 */
export const CART_MONTH_KEYS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
] as const

export const CART_LINE_KINDS = ['bottle', 'edition'] as const
export const CART_COLLECTION_LINES = ['femme', 'homme'] as const

export const CART_ENGRAVING_MAX_LENGTH = 25
export const CART_SIGNATURE_MAX_LENGTH = 30
export const CART_LINE_MAX_QUANTITY = 10
export const CART_MAX_LINES = 20

/** Same-origin path the drawer POSTs to; the API mounts the matching route. */
export const CHECKOUT_SESSIONS_PATH = '/api/v1/checkout/sessions'
/** Error code (HTTP 503) the API returns while checkout is not open. */
export const CHECKOUT_UNAVAILABLE_ERROR_CODE = 'CHECKOUT_UNAVAILABLE'

export const CartMonthKeySchema = z.enum(CART_MONTH_KEYS)
export const CartLineKindSchema = z.enum(CART_LINE_KINDS)
export const CartCollectionLineSchema = z.enum(CART_COLLECTION_LINES)

/**
 * The unrefined field shape, exported so consumers can `.extend()` it (zod
 * refinements do not survive `.extend()`); validate with {@link CartLineSchema}.
 */
export const CartLineFieldsSchema = z.object({
  kind: CartLineKindSchema,
  month: CartMonthKeySchema.optional(),
  line: CartCollectionLineSchema.optional(),
  editionId: z.string().trim().min(1).max(40).optional(),
  engraving: z.string().trim().max(CART_ENGRAVING_MAX_LENGTH).optional(),
  signature: z.string().trim().max(CART_SIGNATURE_MAX_LENGTH).optional(),
  quantity: z.number().int().min(1).max(CART_LINE_MAX_QUANTITY),
})

export function refineCartLine(line: z.infer<typeof CartLineFieldsSchema>, context: z.RefinementCtx): void {
  if (line.kind === 'bottle') {
    if (!line.month) context.addIssue({ code: 'custom', path: ['month'], message: 'MONTH_REQUIRED' })
    if (!line.line) context.addIssue({ code: 'custom', path: ['line'], message: 'LINE_REQUIRED' })
    return
  }
  if (!line.editionId) context.addIssue({ code: 'custom', path: ['editionId'], message: 'EDITION_ID_REQUIRED' })
}

export const CartLineSchema = CartLineFieldsSchema.superRefine(refineCartLine)

export const CheckoutAttributionSchema = z.object({
  subscriber_pseudonymous_id: z.string().trim().min(1).max(256).optional(),
  campaign_pseudonymous_id: z.string().trim().min(1).max(256).optional(),
})

export const CheckoutSessionRequestSchema = z.object({
  lines: z.array(CartLineSchema).min(1).max(CART_MAX_LINES),
  attribution: CheckoutAttributionSchema.optional(),
})

export const CheckoutSessionResponseSchema = z.object({
  url: z.string().url(),
  sessionId: z.string().min(1),
})

export type CartMonthKey = z.infer<typeof CartMonthKeySchema>
export type CartLineKind = z.infer<typeof CartLineKindSchema>
export type CartCollectionLine = z.infer<typeof CartCollectionLineSchema>
export type CartLine = z.infer<typeof CartLineSchema>
export type CheckoutAttribution = z.infer<typeof CheckoutAttributionSchema>
export type CheckoutSessionRequest = z.infer<typeof CheckoutSessionRequestSchema>
export type CheckoutSessionResponse = z.infer<typeof CheckoutSessionResponseSchema>

/** Whole-currency-unit prices (e.g. `{ bottle: 240, edition: 380 }` from `@mist/content`). */
export type CartLinePrices = { readonly bottle: number; readonly edition: number }

/**
 * Unit price of a line in integer cents: a bottle at 240 prices to 24000 and an
 * edition at 380 to 38000. Pure so web (display) and API (charging) agree.
 */
export function priceCartLineCents(line: Pick<CartLine, 'kind'>, prices: CartLinePrices): number {
  const unit = line.kind === 'bottle' ? prices.bottle : prices.edition
  return Math.round(unit * 100)
}
